import { useEffect, useState } from 'react';
import { osg, storageBearer, storageFileId, storageFileUrl, storageOrigin } from '../clients/osg';
let uploadChain = Promise.resolve();
const enqueue = (task) => {
    const run = uploadChain.then(task, task);
    uploadChain = run.catch(() => undefined);
    return run;
};
export const uploadFile = async (fileData, filename = 'jaen-index.json', options) => {
    const file = fileData instanceof File
        ? fileData
        : fileData instanceof Blob
            ? new File([fileData], filename, { type: fileData.type })
            : new File([JSON.stringify(fileData)], filename, {
                type: 'application/json'
            });
    const uploaded = await enqueue(() => osg.mutate(mutation => {
        const result = mutation.upload({
            args: { file, driver: options?.driver ?? null }
        });
        return {
            file_id: result.file_id,
            file_unique_id: result.file_unique_id,
            file_name: result.file_name,
            mime_type: result.mime_type,
            file_size: result.file_size,
            url: result.url,
            thumbUrl: result.thumbUrl,
            thumb: result.thumb
                ? {
                    file_id: result.thumb.file_id,
                    file_unique_id: result.thumb.file_unique_id,
                    file_size: result.thumb.file_size,
                    width: result.thumb.width,
                    height: result.thumb.height
                }
                : null
        };
    }));
    return {
        data: {
            file_id: uploaded.file_id,
            file_unique_id: uploaded.file_unique_id,
            file_name: uploaded.file_name ?? file.name,
            file_size: uploaded.file_size ?? file.size,
            mime_type: uploaded.mime_type ?? file.type,
            ...(uploaded.thumb
                ? {
                    thumb: {
                        file_id: uploaded.thumb.file_id,
                        file_unique_id: uploaded.thumb.file_unique_id,
                        file_size: uploaded.thumb.file_size ?? 0,
                        width: uploaded.thumb.width ?? 0,
                        height: uploaded.thumb.height ?? 0
                    }
                }
                : {})
        },
        fileUrl: uploaded.url,
        fileThumbUrl: uploaded.thumbUrl ?? undefined
    };
};
export const uploadFileFromNode = async (options) => await uploadFile(new File([options.payload], options.fileName || 'jaen-index.json', {
    type: 'application/json'
}), options.fileName, { driver: options.driver });
export const fetchFile = async (idOrUrl) => {
    const fileId = storageFileId(idOrUrl);
    const url = fileId ? storageFileUrl(fileId) : idOrUrl;
    const token = fileId ? storageBearer() : undefined;
    const response = await fetch(url, {
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        mode: 'cors'
    });
    if (!response.ok) {
        throw new Error(`storage gateway answered ${response.status} for ${fileId ?? url}`);
    }
    return await response.blob();
};
export const useFileObjectUrl = (idOrUrl) => {
    const source = idOrUrl ?? undefined;
    const fileId = source ? storageFileId(source) : null;
    const shouldFetch = typeof window !== 'undefined' && Boolean(fileId) && Boolean(storageBearer());
    const [objectUrl, setObjectUrl] = useState(undefined);
    useEffect(() => {
        if (!shouldFetch || !fileId) {
            setObjectUrl(undefined);
            return;
        }
        let cancelled = false;
        let created;
        void fetchFile(fileId)
            .then(blob => {
            if (cancelled)
                return;
            created = URL.createObjectURL(blob);
            setObjectUrl(created);
        })
            .catch(error => {
            console.error('jaen: storage gateway read failed', error);
            if (!cancelled)
                setObjectUrl(undefined);
        });
        return () => {
            cancelled = true;
            if (created)
                URL.revokeObjectURL(created);
        };
    }, [fileId, shouldFetch]);
    if (!shouldFetch)
        return source;
    return objectUrl;
};
export { storageOrigin, storageBearer, storageFileId };
export { storageFileUrl } from '../clients/osg';
